import type { AppConfig } from './config';

/** Thin client for the Cognito hosted UI OAuth2 endpoints (authorization code + PKCE). */

export interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

const SCOPES = 'openid email profile';

function base64url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function randomString(byteLength = 32): string {
  return base64url(crypto.getRandomValues(new Uint8Array(byteLength)));
}

export async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

export function authorizeUrl(config: AppConfig, state: string, codeChallenge: string): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.cognitoClientId,
    redirect_uri: config.redirectUri,
    scope: SCOPES,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });
  return `${config.cognitoDomain}/oauth2/authorize?${params}`;
}

export function logoutUrl(config: AppConfig): string {
  const params = new URLSearchParams({ client_id: config.cognitoClientId, logout_uri: config.logoutUri });
  return `${config.cognitoDomain}/logout?${params}`;
}

async function tokenRequest(config: AppConfig, body: Record<string, string>): Promise<TokenResponse> {
  const response = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: config.cognitoClientId, ...body }),
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Token request failed (${response.status}) ${detail}`);
  }
  return (await response.json()) as TokenResponse;
}

export function exchangeCode(config: AppConfig, code: string, codeVerifier: string): Promise<TokenResponse> {
  return tokenRequest(config, {
    grant_type: 'authorization_code',
    code,
    redirect_uri: config.redirectUri,
    code_verifier: codeVerifier,
  });
}

export function refreshTokens(config: AppConfig, refreshToken: string): Promise<TokenResponse> {
  return tokenRequest(config, { grant_type: 'refresh_token', refresh_token: refreshToken });
}

/** Decodes (does not verify) a JWT payload. API Gateway verifies tokens server-side. */
export function decodeJwt<T = Record<string, unknown>>(token: string): T {
  const payload = token.split('.')[1] ?? '';
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(decodeURIComponent(escape(json))) as T;
}
